<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-grid :cols="3" class="orangehrm-full-width-grid anna-provider-fields">
    <oxd-grid-item v-for="field in enumFields" :key="field.key">
      <search-select
        :label="field.label"
        :options="field.options"
        :model-value="section[field.key] ?? ''"
        placeholder="Vapi default"
        :allow-custom="true"
        @update:model-value="$emit('set', field.key, $event || undefined)"
      />
    </oxd-grid-item>
    <oxd-grid-item v-for="field in numberFields" :key="field.key">
      <oxd-input-field
        :label="field.label"
        :model-value="String(section[field.key] ?? '')"
        :placeholder="`Vapi default · ${field.min}–${field.max}`"
        :title="field.description"
        @update:model-value="setNumber(field, $event)"
      />
      <oxd-text v-if="field.invalid" tag="p" class="anna-field-error">
        {{ field.min }}–{{ field.max }}
      </oxd-text>
    </oxd-grid-item>
  </oxd-grid>
</template>

<script>
import SearchSelect from './SearchSelect.vue';

/** "similarityBoost" -> "Similarity boost" */
export function humanize(key) {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// One provider's own settings (the lists and number ranges Vapi's API description
// gives for it), e.g. ElevenLabs' model, stability and speed. Blank = Vapi's default.
export default {
  name: 'ProviderFields',
  components: {
    'search-select': SearchSelect,
  },
  props: {
    // the model / voice / transcriber object being edited
    section: {type: Object, required: true},
    // {enums: {field: [values]}, numbers: {field: {min, max, description}}}
    spec: {type: Object, default: () => ({enums: {}, numbers: {}})},
    // fields the parent draws itself (e.g. model, voiceId)
    skip: {type: Array, default: () => []},
  },
  emits: ['set'],
  data() {
    return {invalid: {}};
  },
  computed: {
    enumFields() {
      return Object.entries(this.spec.enums || {})
        .filter(([key]) => !this.skip.includes(key))
        .map(([key, values]) => ({
          key,
          label: humanize(key),
          options: [
            {id: '', label: 'Vapi default'},
            ...values.map((v) => ({id: v, label: v})),
          ],
        }));
    },
    numberFields() {
      return Object.entries(this.spec.numbers || {})
        .filter(([key]) => !this.skip.includes(key))
        .map(([key, range]) => ({
          key,
          label: humanize(key),
          ...range,
          invalid: this.invalid[key],
        }));
    },
  },
  methods: {
    setNumber(field, raw) {
      const text = String(raw ?? '').trim();
      if (text === '') {
        this.invalid[field.key] = false;
        this.$emit('set', field.key, undefined);
        return;
      }
      const value = Number(text);
      const ok =
        !Number.isNaN(value) && value >= field.min && value <= field.max;
      this.invalid[field.key] = !ok;
      if (ok) this.$emit('set', field.key, value);
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-provider-fields {
  margin-top: 4px;
}
.anna-field-error {
  font-size: 11px;
  color: #c0392b;
  margin-top: 2px;
}
</style>
